// src/components/ReferralBonusDashboard.jsx
import { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  Text,
} from "@chakra-ui/react";
import { supabase } from "../utils/supabaseClient";
import useBusinessGuard from "../hooks/useBusinessGuard";

/**
 * ReferralBonusDashboard
 * Shows referral bonus history for the current business.
 *
 * IMPORTANT:
 *  - Referral bonuses use UUID identifiers:
 *      referrer_uuid
 *      referred_business_uuid
 *  - This module must use businessIdUuid from useBusinessGuard()
 */
export default function ReferralBonusDashboard() {
  const { businessIdUuid, loading: authLoading } = useBusinessGuard();
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    if (authLoading || !businessIdUuid) return;

    const fetchBonuses = async () => {
      setLoading(true);

      try {
        const { data, error } = await supabase
          .from("referral_audit")
          .select(
            `
            id,
            triggered_at,
            referrer_uuid,
            referred_business_uuid,
            referred_email,
            bonus_amount,
            source
          `
          )
          .or(
            `referrer_uuid.eq.${businessIdUuid},referred_business_uuid.eq.${businessIdUuid}`
          )
          .order("triggered_at", { ascending: false });

        if (error) throw error;

        setBonuses(data || []);
      } catch (err) {
        console.error("Referral bonus error:", err);
        toast({
          title: "Failed to load referral bonuses",
          description: err.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchBonuses();
  }, [authLoading, businessIdUuid, toast]);

  return (
    <Box bg="white" p={4} rounded="md" shadow="md">
      {loading ? (
        <Spinner />
      ) : bonuses.length === 0 ? (
        <Text fontSize="sm" color="gray.500">
          No referral bonuses yet.
        </Text>
      ) : (
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Role</Th>
              <Th>Email</Th>
              <Th>Amount</Th>
              <Th>Source</Th>
            </Tr>
          </Thead>
          <Tbody>
            {bonuses.map((bonus) => {
              const isReferrer = bonus.referrer_uuid === businessIdUuid;

              return (
                <Tr key={bonus.id}>
                  <Td>
                    {bonus.triggered_at
                      ? new Date(bonus.triggered_at).toLocaleDateString()
                      : "—"}
                  </Td>

                  <Td>{isReferrer ? "Referrer" : "Referred"}</Td>

                  <Td>{bonus.referred_email || "—"}</Td>

                  <Td>
                    {bonus.bonus_amount !== null &&
                    bonus.bonus_amount !== undefined
                      ? `£${Number(bonus.bonus_amount).toFixed(2)}`
                      : "—"}
                  </Td>

                  <Td>{bonus.source || "—"}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}
